// ViewProject.jsx
import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import axios from "axios";
import AdminNavbar from "../Layout/AdminNavbar";
import { FaTrash } from "react-icons/fa";
import API_BASE_URL from "../config";

/* ──────────────────────────────────────────────
   Custom Searchable Dropdown (from AdminManageTeams)
────────────────────────────────────────────── */
const CustomSelect = ({ value, onChange, options, placeholder }) => {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState("");
    const [coords, setCoords] = useState({});
    const triggerRef = useRef(null);
    const inputRef = useRef(null);

    const selected = options.find((o) => String(o.value) === String(value));

    const recalc = () => {
        if (!triggerRef.current) return;
        const r = triggerRef.current.getBoundingClientRect();
        const spaceBelow = window.innerHeight - r.bottom;
        const openUp = spaceBelow < 270 && r.top > 270;
        setCoords({
            left: r.left,
            width: r.width,
            ...(openUp ? { bottom: window.innerHeight - r.top + 5 } : { top: r.bottom + 5 }),
        });
    };

    const handleOpen = () => { recalc(); setOpen(true); };

    useEffect(() => {
        if (!open) return;
        const handler = (e) => {
            if (
                triggerRef.current && !triggerRef.current.contains(e.target) &&
                !e.target.closest(".csel-panel")
            ) { setOpen(false); setSearch(""); }
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, [open]);

    useEffect(() => {
        if (open && inputRef.current) setTimeout(() => inputRef.current?.focus(), 0);
    }, [open]);

    const filtered = options.filter((o) =>
        o.label.toLowerCase().includes(search.toLowerCase())
    );

    const handleSelect = (opt) => { onChange(opt.value); setOpen(false); setSearch(""); };

    const panel = open ? (
        <div className="csel-panel" style={{ position: "fixed", zIndex: 99999, ...coords }}>
            <div className="csel-search-wrap">
                <svg className="csel-search-icon" width="13" height="13" viewBox="0 0 13 13" fill="none">
                    <circle cx="5.5" cy="5.5" r="4" stroke="#A8A29E" strokeWidth="1.4" />
                    <path d="M9 9l2.5 2.5" stroke="#A8A29E" strokeWidth="1.4" strokeLinecap="round" />
                </svg>
                <input ref={inputRef} className="csel-search" placeholder="Search…" value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
            <ul className="csel-list">
                {filtered.length === 0 ? (
                    <li className="csel-empty">No results</li>
                ) : (
                    filtered.map((opt) => (
                        <li
                            key={opt.value}
                            className={`csel-option ${String(opt.value) === String(value) ? "csel-option--active" : ""}`}
                            onMouseDown={() => handleSelect(opt)}
                        >
                            <span className="csel-option-label">{opt.label}</span>
                            {String(opt.value) === String(value) && (
                                <svg className="csel-check" width="13" height="13" viewBox="0 0 13 13" fill="none">
                                    <path d="M2 6.5l3.5 3.5 5.5-6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            )}
                        </li>
                    ))
                )}
            </ul>
        </div>
    ) : null;

    return (
        <div className="csel-root">
            <button
                ref={triggerRef}
                type="button"
                className={`csel-trigger ${open ? "csel-trigger--open" : ""}`}
                onClick={() => (open ? (setOpen(false), setSearch("")) : handleOpen())}
            >
                <span className={`csel-trigger-label ${!selected ? "csel-placeholder" : ""}`}>
                    {selected ? selected.label : placeholder}
                </span>
                <svg className={`csel-chevron ${open ? "csel-chevron--up" : ""}`} width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <path d="M3 5l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
            </button>
            {createPortal(panel, document.body)}
        </div>
    );
};

/* ──────────────────────────────────────────────
   Collapsible Section
────────────────────────────────────────────── */
const Section = ({ title, children }) => {
    const [open, setOpen] = useState(true);
    return (
        <div className="mb-3 border">
            <div
                className="fw-bold p-2 border-bottom d-flex justify-content-between align-items-center"
                style={{ cursor: "pointer", backgroundColor: "#DFE4EB" }}
                onClick={() => setOpen(!open)}
            >
                <span>{title}</span>
                <span>{open ? "▾" : "▸"}</span>
            </div>
            {open && <div className="row g-3 p-3">{children}</div>}
        </div>
    );
};

/* ──────────────────────────────────────────────
   Main Component
────────────────────────────────────────────── */
export default function ViewProject() {
    const { id } = useParams();
    const navigate = useNavigate();

    // Project state
    const [project, setProject] = useState(null);
    const [activeTab, setActiveTab] = useState("details");
    const [activeSubTab, setActiveSubTab] = useState("systemNotes");
    const [systemNotes, setSystemNotes] = useState([]);
    const [projectIds, setProjectIds] = useState([]);
    const [navbarColor, setNavbarColor] = useState("#080235");

    // Team state
    const [teamMembers, setTeamMembers] = useState([]);
    const [teams, setTeams] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [showAddModal, setShowAddModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedMember, setSelectedMember] = useState(null);
    const [teamFormData, setTeamFormData] = useState({ teamId: "", employeeId: "", reporterId: "", projectId: "" });
    const [sortConfig, setSortConfig] = useState({ key: "", direction: "asc" });

    const token = localStorage.getItem("token");

    // Bootstrap loader
    useEffect(() => {
        const bootstrapLink = document.createElement("link");
        bootstrapLink.rel = "stylesheet";
        bootstrapLink.href = "https://cdn.jsdelivr.net/npm/bootstrap@5.3.2/dist/css/bootstrap.min.css";
        document.head.appendChild(bootstrapLink);
        const iconsLink = document.createElement("link");
        iconsLink.rel = "stylesheet";
        iconsLink.href = "https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.1/font/bootstrap-icons.css";
        document.head.appendChild(iconsLink);
        return () => { bootstrapLink.remove(); iconsLink.remove(); };
    }, []);

    // Auth check
    useEffect(() => {
        const roles = JSON.parse(localStorage.getItem("roles") || "[]");
        if (!token) { toast.error("You must be logged in."); navigate("/"); return; }
        if (!roles.includes("ROLE_ADMIN")) { toast.error("Unauthorized."); navigate("/"); return; }
    }, [navigate, token]);

    // Navbar color
    useEffect(() => {
        const userId = localStorage.getItem("userId");
        if (!token || !userId) return;
        fetch(`${API_BASE_URL}/api/appearance/${userId}`, { headers: { Authorization: `Bearer ${token}` } })
            .then((r) => r.json())
            .then((d) => { if (d?.navbarColor) setNavbarColor(d.navbarColor); })
            .catch(() => { });
    }, [token]);

    // Load all project IDs for navigation
    useEffect(() => {
        fetch(`${API_BASE_URL}/api/projects`, { headers: { Authorization: `Bearer ${token}` } })
            .then((r) => r.ok ? r.json() : [])
            .then((data) => {
                const list = Array.isArray(data) ? data : data.content || [];
                const ids = list.map((p) => Number(p.id)).filter((i) => !isNaN(i)).sort((a, b) => a - b);
                setProjectIds(ids);
            })
            .catch(() => { });
    }, [token]);

    // Load project
    useEffect(() => {
        if (!id) return;
        fetch(`${API_BASE_URL}/api/projects/${id}`, { headers: { Authorization: `Bearer ${token}` } })
            .then((r) => r.json())
            .then((data) => setProject(data))
            .catch(() => toast.error("Failed to load project."));
    }, [id, token]);

    useEffect(() => {

        if (activeTab !== "system" || !id) return;

        axios.get(
            `${API_BASE_URL}/api/system-notes?entityType=PROJECT&entityId=${id}`,
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            }
        )
            .then((res) => {
                setSystemNotes(res.data || []);
            })
            .catch(() => {
                toast.error("Failed to load system notes.");
            });

    }, [activeTab, id, token]);

    // Load team members & teams — only when team tab is first opened
    const [teamDataLoaded, setTeamDataLoaded] = useState(false);

    useEffect(() => {
        if (activeTab !== "team" || teamDataLoaded || !id) return;
        const axiosConfig = { headers: { Authorization: `Bearer ${token}` } };
        Promise.all([
            axios.get(`${API_BASE_URL}/api/manageteam`, axiosConfig),
            axios.get(`${API_BASE_URL}/api/teams`, axiosConfig),
        ])
            .then(([teamRes, teamsRes]) => {
                const all = Array.isArray(teamRes.data) ? teamRes.data : [];
                setTeamMembers(all);
                setTeams(teamsRes.data);
                setTeamDataLoaded(true);
            })
            .catch(() => toast.error("Failed to load team data."));
    }, [activeTab, teamDataLoaded, id, token]);

    // Load employees when add modal opens
    useEffect(() => {
        if (!showAddModal) return;
        axios.get(`${API_BASE_URL}/api/employees/active`, { headers: { Authorization: `Bearer ${token}` } })
            .then((res) => setEmployees(res.data))
            .catch(() => toast.error("Failed to load employees."));
    }, [showAddModal, token]);

    /* ── Helpers ── */
    const formatDate = (iso) => iso ? new Date(iso).toLocaleDateString("en-GB") : "—";
    const formatDateTime = (iso) => {
        if (!iso) return "";
        return new Date(iso).toLocaleString("en-IN", { year: "numeric", month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" });
    };
    const formatCurrency = (amt, cur) => amt ? `${cur || ""} ${Number(amt).toLocaleString("en-IN")}` : "—";
    const initials = (first, last) => `${(first?.[0] || "").toUpperCase()}${(last?.[0] || "").toUpperCase()}`;

    const getStatusBadge = (status) => {
        const map = { Active: "success", Planning: "primary", "On Hold": "warning", Completed: "info", Archived: "secondary", Cancelled: "danger" };
        return <span className={`badge bg-${map[status] || "secondary"}`}>{status || "—"}</span>;
    };
    const getPriorityBadge = (priority) => {
        const map = { Low: "success", Medium: "warning", High: "danger", Critical: "dark" };
        return <span className={`badge bg-${map[priority] || "secondary"}`}>{priority || "—"}</span>;
    };
    const getVisBadge = (vis) => {
        const map = { Private: "secondary", Internal: "primary", Public: "success" };
        return <span className={`badge bg-${map[vis] || "secondary"}`}>{vis || "—"}</span>;
    };

    /* ── Team Sort ── */
    const sortData = (key) => {
        let direction = "asc";
        if (sortConfig.key === key && sortConfig.direction === "asc") direction = "desc";
        setSortConfig({ key, direction });
    };
    const getVal = (m, key) => {
        switch (key) {
            case "employee": return m.employeeFirstName ?? "";
            case "team": return m.teamName ?? "";
            case "reporter": return m.reporterFirstName ?? "";
            default: return "";
        }
    };
    const arrow = (key) => sortConfig.key === key ? (sortConfig.direction === "asc" ? " ↑" : " ↓") : " ⇅";

    // Filter by project name (matches AdminManageTeams logic — API returns projectName, not projectId)
    const projectTeamMembers = project
        ? teamMembers.filter((m) => m.projectName === project.name)
        : [];

    let sortedMembers = [...projectTeamMembers];
    if (sortConfig.key) {
        sortedMembers.sort((a, b) => {
            const vA = getVal(a, sortConfig.key), vB = getVal(b, sortConfig.key);
            if (vA < vB) return sortConfig.direction === "asc" ? -1 : 1;
            if (vA > vB) return sortConfig.direction === "asc" ? 1 : -1;
            return 0;
        });
    }

    /* ── Team Add ── */
    const handleAddMember = async (e) => {
        e.preventDefault();
        const { teamId, employeeId, reporterId } = teamFormData;
        if (!teamId || !employeeId || !reporterId) { toast.error("All fields are required."); return; }
        try {
            const payload = { ...teamFormData, projectId: id };
            const res = await axios.post(`${API_BASE_URL}/api/manageteam`, payload, {
                headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
            });
            setTeamMembers((prev) => [...prev, res.data]);
            toast.success("Team member added!");
            setTeamFormData({ teamId: "", employeeId: "", reporterId: "", projectId: "" });
            setShowAddModal(false);
        } catch (err) {
            toast.error(err.response?.data || "Something went wrong.");
        }
    };

    /* ── Team Delete ── */
    const confirmDelete = async () => {
        if (!selectedMember) return;
        try {
            await axios.delete(`${API_BASE_URL}/api/manageteam/${selectedMember.assignmentId}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setTeamMembers((prev) => prev.filter((m) => m.assignmentId !== selectedMember.assignmentId));
            toast.success(`${selectedMember.employeeFirstName} ${selectedMember.employeeLastName} removed.`);
            setShowDeleteModal(false);
            setSelectedMember(null);
        } catch {
            toast.error("Error removing member.");
        }
    };

    const teamOptions = [...teams].sort((a, b) => a.name.localeCompare(b.name)).map((t) => ({ value: t.id, label: t.name }));
    const employeeOptions = employees.map((e) => ({ value: e.id, label: `${e.firstName} ${e.lastName}` }));

    if (!project) return <div className="p-4">Loading...</div>;
    const p = project;

    return (
        <>
            <div className="fixed-top"><AdminNavbar /></div>
            <div className="container-fluid my-4" style={{ paddingTop: "90px" }}>

                {/* ── Header ── */}
                <div className="border-bottom pb-2 mb-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                        <div className="d-flex align-items-center">
                            <i className="bi bi-kanban fs-3 me-2 text-primary"></i>
                            <h5 className="m-0 me-3">Project</h5>
                        </div>
                        <div className="d-flex align-items-center text-muted">
                            <i
                                className="bi bi-arrow-left-circle me-3"
                                style={{ cursor: "pointer", fontSize: "1.3rem", color: "#0d6efd" }}
                                title="Previous Project"
                                onClick={() => {
                                    const idx = projectIds.indexOf(parseInt(id));
                                    if (idx > 0) navigate(`/viewproject/${projectIds[idx - 1]}`);
                                    else toast.info("This is the first project.");
                                }}
                            />
                            <i
                                className="bi bi-arrow-right-circle me-3"
                                style={{ cursor: "pointer", fontSize: "1.3rem", color: "#0d6efd" }}
                                title="Next Project"
                                onClick={() => {
                                    const idx = projectIds.indexOf(parseInt(id));
                                    if (idx < projectIds.length - 1) navigate(`/viewproject/${projectIds[idx + 1]}`);
                                    else toast.info("This is the last project.");
                                }}
                            />
                            <span className="me-2" style={{ cursor: "pointer", color: "blue" }} onClick={() => navigate("/allprojects")}>List</span>
                        </div>
                    </div>

                    <div className="d-flex align-items-center mb-2">
                        <div style={{
                            width: 42, height: 42, borderRadius: 8, flexShrink: 0,
                            background: p.accentColor || "#1e3a5f", color: "#e8f0f8",
                            display: "flex", alignItems: "center", justifyContent: "center",
                            fontSize: 20, marginRight: 12,
                        }}>
                            {p.icon || p.name?.slice(0, 2).toUpperCase()}
                        </div>
                        <h4 className="m-0 me-3">{p.name}</h4>
                        {/* {p.key && (
                            <span className="me-2" style={{ background: "#e8f0f8", color: "#1e3a5f", borderRadius: 4, padding: "2px 10px", fontFamily: "monospace", fontSize: 13 }}>
                                {p.key}
                            </span>
                        )} */}
                        {p.status && p.status !== "Active" && getStatusBadge(p.status)}
                        {/* <span className="ms-2">{getPriorityBadge(p.priority)}</span>
                        <span className="ms-2">{getVisBadge(p.visibility)}</span> */}
                    </div>

                    <div className="d-flex align-items-center mb-2">
                        <button className="btn btn-primary me-2" onClick={() => navigate(`/editproject/${id}`)}>Edit</button>
                        <button className="btn btn-light btn-sm me-2" onClick={() => navigate("/allprojects")}>Back</button>
                    </div>
                </div>

                {/* ── Basic Information ── */}
                <Section title="Basic Information">
                    <div className="col-md-3"><strong>Project Name</strong><br />{p.name}</div>
                    <div className="col-md-3"><strong>Project Key</strong><br /><span style={{ fontFamily: "monospace" }}>{p.key || "—"}</span></div>
                    <div className="col-md-3"><strong>Project Type</strong><br />{p.projectType || "—"}</div>
                    <div className="col-md-3"><strong>Industry</strong><br />{p.industry || "—"}</div>
                    <div className="col-md-3"><strong>Category</strong><br />{p.category || "—"}</div>
                    <div className="col-md-3"><strong>Reference No.</strong><br /><span style={{ fontFamily: "monospace" }}>{p.referenceNumber || "—"}</span></div>
                    <div className="col-md-3"><strong>Customer</strong><br />{p.customerName || p.customer || "—"}</div>
                    <div className="col-md-3"><strong>Owner</strong><br />{p.ownerName || p.owner || "—"}</div>
                    {p.description && (
                        <div className="col-md-12">
                            <strong>Description</strong>
                            <div className="mt-1 p-2 border rounded bg-light" style={{ whiteSpace: "pre-wrap" }}>{p.description}</div>
                        </div>
                    )}
                </Section>

                {/* ── Tabs ── */}
                <ul className="nav nav-tabs mb-2 fw-semibold" style={{ backgroundColor: navbarColor }}>
                    {[
                        ["details", "Status & Timeline"],
                        ["budget", "Budget"],
                        ["tags", "Tags"],
                        ["team", `Manage Team ${projectTeamMembers.length > 0 ? `(${projectTeamMembers.length})` : ""}`],
                        ["system", "System Information"],
                    ].map(([tabId, label]) => (
                        <li className="nav-item" key={tabId}>
                            <button
                                type="button"
                                className={`nav-link ${activeTab === tabId ? "active" : ""}`}
                                onClick={() => setActiveTab(tabId)}
                                style={{
                                    color: activeTab === tabId ? navbarColor : "#fff",
                                    backgroundColor: activeTab === tabId ? "#fff" : "transparent",
                                    fontWeight: activeTab === tabId ? "600" : "normal",
                                }}
                            >
                                {label}
                            </button>
                        </li>
                    ))}
                </ul>

                <div className="border p-3">

                    {/* ── Status & Timeline ── */}
                    {activeTab === "details" && (
                        <>
                            <Section title="Status & Governance">
                                <div className="col-md-3"><strong>Status</strong><br />{getStatusBadge(p.status)}</div>
                                <div className="col-md-3"><strong>Priority</strong><br />{getPriorityBadge(p.priority)}</div>
                                <div className="col-md-3"><strong>Visibility</strong><br />{getVisBadge(p.visibility)}</div>
                            </Section>
                            <Section title="Timeline">
                                <div className="col-md-3"><strong>Start Date</strong><br />{formatDate(p.startDate)}</div>
                                <div className="col-md-3"><strong>End Date</strong><br />{formatDate(p.endDate)}</div>
                                <div className="col-md-3"><strong>Created</strong><br />{formatDate(p.createdAt)}</div>
                                <div className="col-md-3"><strong>Last Updated</strong><br />{formatDate(p.updatedAt)}</div>
                            </Section>
                        </>
                    )}

                    {/* ── Budget ── */}
                    {activeTab === "budget" && (
                        <Section title="Budget">
                            <div className="col-md-3"><strong>Estimated Budget</strong><br />{formatCurrency(p.estimatedBudget, p.budgetCurrency)}</div>
                            <div className="col-md-3"><strong>Currency</strong><br />{p.budgetCurrency || "—"}</div>
                        </Section>
                    )}

                    {/* ── Tags ── */}
                    {activeTab === "tags" && (
                        <div className="p-2">
                            <strong>Tags</strong>
                            <div className="mt-2 d-flex flex-wrap gap-2">
                                {p.tags && p.tags.length > 0 ? (
                                    p.tags.map((t) => (
                                        <span key={t} style={{ background: "#e8f0f8", color: "#1e3a5f", border: "1px solid #c8d8e8", borderRadius: 20, padding: "3px 12px", fontSize: 13 }}>
                                            {t}
                                        </span>
                                    ))
                                ) : (
                                    <span className="text-muted">No tags added.</span>
                                )}
                            </div>
                        </div>
                    )}

                    {/* ── Manage Team ── */}
                    {activeTab === "team" && (
                        <div>
                            {/* Sub-header */}
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <div>
                                    <h6 className="mb-0 fw-semibold">Team Members</h6>
                                    <small className="text-muted">{sortedMembers.length} member{sortedMembers.length !== 1 ? "s" : ""} assigned to this project</small>
                                </div>
                                <button
                                    className="btn btn-primary btn-sm"
                                    onClick={() => setShowAddModal(true)}
                                >
                                    <i className="bi bi-person-plus me-1"></i> Add Member
                                </button>
                            </div>

                            {sortedMembers.length === 0 ? (
                                <div className="text-center py-5 text-muted">
                                    <i className="bi bi-people fs-1 d-block mb-2"></i>
                                    <p className="mb-1 fw-semibold">No team members assigned</p>
                                    <small>Click "Add Member" to assign someone to this project.</small>
                                </div>
                            ) : (
                                <div className="table-responsive">
                                    <table className="table table-hover table-sm align-middle">
                                        <thead>
                                            <tr style={{ backgroundColor: "#DFE4EB" }}>
                                                <th style={{ backgroundColor: "#DFE4EB" }}>Sr.no</th>
                                                {[
                                                    { key: "employee", label: "Employee" },
                                                    { key: "team", label: "Team" },
                                                    { key: "reporter", label: "Manager" },
                                                ].map(({ key, label }) => (
                                                    <th
                                                        key={key}
                                                        style={{ cursor: "pointer", backgroundColor: "#DFE4EB" }}
                                                        onClick={() => sortData(key)}
                                                    >
                                                        {label}
                                                        <span className="ms-1 text-muted" style={{ fontSize: 11 }}>{arrow(key)}</span>
                                                    </th>
                                                ))}
                                                {/* <th style={{ backgroundColor: "#DFE4EB" }}>Action</th> */}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {sortedMembers.map((m, idx) => (
                                                <tr key={m.assignmentId}>
                                                    <td className="text-muted" style={{ fontSize: 13 }}>{idx + 1}</td>
                                                    <td>
                                                        <div className="d-flex align-items-center gap-2">
                                                            <div style={{
                                                                width: 32, height: 32, borderRadius: "50%", flexShrink: 0,
                                                                background: "#1e3a5f", color: "#fff",
                                                                display: "flex", alignItems: "center", justifyContent: "center",
                                                                fontSize: 12, fontWeight: 600, overflow: "hidden",
                                                            }}>
                                                                {m.employeePhotoPath ? (
                                                                    <img src={`${API_BASE_URL}/uploads/${m.employeePhotoPath}`} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                                                                ) : initials(m.employeeFirstName, m.employeeLastName)}
                                                            </div>
                                                            <span className="fw-medium">{m.employeeFirstName} {m.employeeLastName}</span>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <span className="badge" style={{ background: "#e8f0f8", color: "#1e3a5f", fontWeight: 500 }}>{m.teamName}</span>
                                                    </td>
                                                    <td>{m.reporterFirstName} {m.reporterLastName}</td>
                                                    {/* <td>
                            <button
                              className="btn btn-sm btn-outline-danger"
                              title="Remove member"
                              onClick={() => { setSelectedMember(m); setShowDeleteModal(true); }}
                            >
                              <FaTrash size={12} />
                            </button>
                          </td> */}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    )}

                    {/* ── System Information ── */}
                    {activeTab === "system" && (
                        <div className="p-3 border rounded bg-white">
                            <div className="mb-3">
                                {/* <div className="form-check mb-2">
                                    <input type="checkbox" className="form-check-input" checked={p.status === "Archived"} disabled readOnly />
                                    <label className="form-check-label fw-bold ms-2">ARCHIVED</label>
                                </div> */}
                                <div className="text-muted small">
                                    <strong>Date Created:</strong> {formatDateTime(p.createdAt)}
                                </div>
                            </div>
                            <ul className="nav nav-tabs mb-3">
                                <li className="nav-item">
                                    <button className={`nav-link ${activeSubTab === "systemNotes" ? "active" : ""}`} onClick={() => setActiveSubTab("systemNotes")}>
                                        System Notes
                                    </button>
                                </li>
                            </ul>
                            {activeSubTab === "systemNotes" && (
                                <table className="table table-sm align-middle">
                                    <thead>
                                        <tr>
                                            <th style={{ backgroundColor: "#DFE4EB" }}>Date</th>
                                            <th style={{ backgroundColor: "#DFE4EB" }}>Set By</th>
                                            <th style={{ backgroundColor: "#DFE4EB" }}>Action</th>
                                            <th style={{ backgroundColor: "#DFE4EB" }}>Field</th>
                                            <th style={{ backgroundColor: "#DFE4EB" }}>Old Value</th>
                                            <th style={{ backgroundColor: "#DFE4EB" }}>New Value</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {systemNotes.length === 0 ? (
                                            <tr><td colSpan="5" className="text-muted text-center">No records to show.</td></tr>
                                        ) : (
                                            systemNotes.map((note, i) => (
                                                <tr key={i}>
                                                    <td>{formatDateTime(note.changedAt)}</td>
                                                    <td>{note.changedBy}</td>
                                                    <td>
                                                        <span
                                                            className={`badge ${note.action === "CREATED"
                                                                    ? "bg-success"
                                                                    : note.action === "TEAM_MEMBER_ADDED"
                                                                        ? "bg-primary"
                                                                        : note.action === "TEAM_MEMBER_REMOVED"
                                                                            ? "bg-danger"
                                                                            : "bg-warning text-dark"
                                                                }`}
                                                        >
                                                            {note.action
                                                                ?.replaceAll("_", " ")
                                                                ?.toUpperCase()}
                                                        </span>
                                                    </td>

                                                    <td>{note.fieldName}</td>
                                                    <td>{note.oldValue || "—"}</td>
                                                    <td>{note.newValue || "—"}</td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* ── Add Member Modal ── */}
            {showAddModal && (
                <>
                    <div className="modal-backdrop fade show" style={{ zIndex: 1040 }} onClick={() => setShowAddModal(false)} />
                    <div className="modal fade show d-block" style={{ zIndex: 1050 }} tabIndex="-1">
                        <div className="modal-dialog modal-dialog-centered">
                            <div className="modal-content">
                                <div className="modal-header" style={{ backgroundColor: "#f8f9fa" }}>
                                    <div>
                                        <h5 className="modal-title fw-semibold mb-0">Add Team Member</h5>
                                        <small className="text-muted">Assign a member to <strong>{p.name}</strong></small>
                                    </div>
                                    <button type="button" className="btn-close" onClick={() => setShowAddModal(false)} />
                                </div>
                                <div className="modal-body">
                                    <div className="row g-3">
                                        <div className="col-md-6">
                                            <label className="form-label fw-medium">Team <span className="text-danger">*</span></label>
                                            <CustomSelect
                                                value={teamFormData.teamId}
                                                onChange={(v) => setTeamFormData((prev) => ({ ...prev, teamId: v }))}
                                                options={teamOptions}
                                                placeholder="Select Team"
                                            />
                                        </div>
                                        <div className="col-md-6">
                                            <label className="form-label fw-medium">Employee <span className="text-danger">*</span></label>
                                            <CustomSelect
                                                value={teamFormData.employeeId}
                                                onChange={(v) => setTeamFormData((prev) => ({ ...prev, employeeId: v }))}
                                                options={employeeOptions}
                                                placeholder="Select Employee"
                                            />
                                        </div>
                                        <div className="col-md-12">
                                            <label className="form-label fw-medium">Reporter / Manager <span className="text-danger">*</span></label>
                                            <CustomSelect
                                                value={teamFormData.reporterId}
                                                onChange={(v) => setTeamFormData((prev) => ({ ...prev, reporterId: v }))}
                                                options={employeeOptions}
                                                placeholder="Select Reporter"
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button className="btn btn-primary" onClick={handleAddMember}>Save</button>
                                    <button className="btn btn-light" onClick={() => setShowAddModal(false)}>Cancel</button>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}

            {/* ── Delete Confirm Modal ── */}
            {showDeleteModal && selectedMember && (
                <>
                    <div className="modal-backdrop fade show" style={{ zIndex: 1040 }} onClick={() => setShowDeleteModal(false)} />
                    <div className="modal fade show d-block" style={{ zIndex: 1050 }} tabIndex="-1">
                        <div className="modal-dialog modal-dialog-centered modal-sm">
                            <div className="modal-content">
                                <div className="modal-header border-0 pb-0">
                                    <div>
                                        <h5 className="modal-title fw-semibold text-danger mb-0">Remove Member</h5>
                                        <small className="text-muted">This action cannot be undone.</small>
                                    </div>
                                    <button type="button" className="btn-close" onClick={() => setShowDeleteModal(false)} />
                                </div>
                                <div className="modal-body pt-2">
                                    <div className="d-flex align-items-center gap-3 p-3 rounded" style={{ background: "#f8f9fa" }}>
                                        <div style={{
                                            width: 44, height: 44, borderRadius: "50%", flexShrink: 0,
                                            background: "#1e3a5f", color: "#fff",
                                            display: "flex", alignItems: "center", justifyContent: "center",
                                            fontWeight: 600, fontSize: 14, overflow: "hidden",
                                        }}>
                                            {selectedMember.employeePhotoPath ? (
                                                <img src={`${API_BASE_URL}/uploads/${selectedMember.employeePhotoPath}`} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                                            ) : initials(selectedMember.employeeFirstName, selectedMember.employeeLastName)}
                                        </div>
                                        <div>
                                            <p className="mb-0 fw-semibold">{selectedMember.employeeFirstName} {selectedMember.employeeLastName}</p>
                                            <small className="text-muted">{selectedMember.teamName} · Reports to: {selectedMember.reporterFirstName} {selectedMember.reporterLastName}</small>
                                        </div>
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button className="btn btn-danger btn-sm" onClick={confirmDelete}>Remove</button>
                                    <button className="btn btn-light btn-sm" onClick={() => setShowDeleteModal(false)}>Cancel</button>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </>
    );
}